import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../hooks/useTranslations";
import { AdminRoofType } from "../../../../../../http/patrol/types/roofTypes";
import { useRoofTypesApi } from "../../../../../../http/patrol/useRoofTypes";
import { PaginationQuery } from "../../../../../../types";
import { formatBoolean } from "../../../../../../utils";
import Button from "../../../../../ui/Button";
import FlexContent from "../../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../ui/ModalDialog";
import SearchBar from "../../../../../ui/SearchBar";
import Table from "../../../../../ui/Table";
import Visibility from "../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import NewRoofType from "./NewRoofType";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const RoofTypes: FC<Props> = ({ active }) => {
  const { systemId } = useParams<{ systemId: string }>();
  const { categoryId } = useParams<{ categoryId: string }>();
  const i18n = useI18n();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "roofTypes" });
  const aborter = useRef(new AbortController());
  const roofTypesApi = useRoofTypesApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await roofTypesApi.updateAdminRoofTypeVisibility(id, { visibility }, { categoryId, systemId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (roofTypeId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await roofTypesApi.updateAdminRoofTypeOrder(roofTypeId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AdminRoofType>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminRoofType>("patrol.roof_type");
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "roofTypes");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = roofTypesApi.useAdminCategoryRoofTypes(categoryId!, query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.roofTypes.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.roofTypes.search} value={searchTerm} onChange={handleSearchChange} />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminRoofType>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.common.hasHips}</th>
            <th align="left">{i18n.translation.common.canBeCustom}</th>
            <th align="left">{i18n.translation.common.canBeMultiple}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/roof_categories/${categoryId}/roof_types/${item.id}`}
              >
                {item.name}
              </Link>
            </td>
            <td>{formatBoolean(i18n.translation, item.hasHips)}</td>
            <td>{formatBoolean(i18n.translation, item.canBeCustom)}</td>
            <td>{formatBoolean(i18n.translation, item.canBeMultiple)}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) =>
          navigate(`/admin/systems/${systemId}/roof_categories/${categoryId}/roof_types/${item.id}`)
        }
        rowActions={(item) => [
          getEditAction(i18n, () =>
            navigate(`/admin/systems/${systemId}/roof_categories/${categoryId}/roof_types/${item.id}`),
          ),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.roofTypes.createNew}>
          <NewRoofType />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.roofTypes.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                roofTypesApi
                  .deleteAdminRoofType(itemToDelete.id, categoryId)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.roofTypes.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default RoofTypes;
